import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useWebRTC } from './WebRTCContext';
import { coordinateMapper } from '../utils/CoordinateMapper';

// Routes where video should default to EXPANDED. Only the dedicated live-view
// page qualifies — Timeline (an event list) and Limitations (a settings page)
// were here too, which made the video panel auto-expand and bury their actual
// content on load. The panel stays a pinnable extra everywhere else (the
// operator can still expand it manually; that choice persists per route).
const VIDEO_PRIMARY_ROUTES = [
  '/app/monitoring/screen',
];

const ScreenShareContext = createContext(null);

export const useScreenShare = () => {
  const context = useContext(ScreenShareContext);
  if (!context) {
    throw new Error('useScreenShare must be used within a ScreenShareProvider');
  }
  return context;
};

/**
 * ScreenShareProvider - Manages screen share video state and interactions
 *
 * Responsibilities:
 * - Video element creation and lifecycle
 * - Coordinate mapping for click detection
 * - Window selection state
 * - Panel visibility and position
 */
export const ScreenShareProvider = ({ children }) => {
  const { isConnected, featureManager, sendFeatureMessage } = useWebRTC();
  const location = useLocation();

  // Panel state
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(() => {
    // Default collapsed unless on a video-primary route
    const route = location.pathname.replace(/\/+$/, '');
    const stored = localStorage.getItem(`screenShareCollapsed:${route}`);
    if (stored !== null) return stored === 'true';
    return !VIDEO_PRIMARY_ROUTES.some(r => route.startsWith(r));
  });
  const [panelPosition, setPanelPosition] = useState('top'); // 'top' dropdown layout

  // Video state
  const [hasScreenShare, setHasScreenShare] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState(16 / 9); // Persisted across route changes

  // Selected window state (for overlay rendering)
  const [selectedWindow, setSelectedWindow] = useState(null);
  const [foregroundWindow, setForegroundWindow] = useState(null);

  // Native screen resolution from desktop
  const [nativeResolution, setNativeResolution] = useState({ width: null, height: null });

  // Refs
  const screenShareVideoRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const containerRef = useRef(null);

  /**
   * Register the screen share video element
   */
  const registerScreenShareVideo = useCallback((videoElement) => {
    screenShareVideoRef.current = videoElement;
    coordinateMapper.setVideoElement(videoElement);

    if (videoElement) {
      videoElement.onloadedmetadata = () => {
        console.log('[ScreenShare] Video metadata loaded:', {
          videoWidth: videoElement.videoWidth,
          videoHeight: videoElement.videoHeight
        });
        setIsVideoReady(true);
      };
    }
  }, []);

  /**
   * Register the camera video element
   */
  const registerCameraVideo = useCallback((videoElement) => {
    cameraVideoRef.current = videoElement;
  }, []);

  /**
   * Update native screen resolution (called when desktop sends dimensions)
   */
  const updateNativeResolution = useCallback((width, height) => {
    setNativeResolution({ width, height });
    coordinateMapper.setNativeResolution(width, height);
  }, []);

  /**
   * Handle click on screen share video
   * Translates coordinates and notifies window-control feature
   */
  const handleScreenClick = useCallback((event) => {
    const coords = coordinateMapper.clientToScreen(event.clientX, event.clientY);

    if (!coords) {
      console.warn('[ScreenShare] Could not translate click coordinates');
      return;
    }

    console.log('[ScreenShare] Click at screen coordinates:', coords.screenX, coords.screenY);

    // Get window-control feature and send message
    const windowControl = featureManager?.getFeature('window-control');
    if (windowControl) {
      // Find the peer ID from connected peers
      // For now, broadcast to all peers
      windowControl.sendMessage('get_window_at_position', {
        x: coords.screenX,
        y: coords.screenY
      });
    }
  }, [featureManager]);

  /**
   * Convert screen bounds to display bounds for overlay rendering
   */
  const getDisplayBounds = useCallback((screenBounds) => {
    return coordinateMapper.screenBoundsToDisplay(screenBounds);
  }, []);

  /**
   * Toggle panel visibility
   */
  const togglePanel = useCallback(() => {
    setIsPanelVisible(prev => !prev);
  }, []);

  /**
   * Explicitly set panel visibility (used by the dedicated Screen page to
   * guarantee the live view is shown even if the operator had hidden the bar).
   */
  const setPanelVisible = useCallback((visible) => {
    setIsPanelVisible(!!visible);
  }, []);

  /**
   * Toggle panel collapsed state and persist per-route
   */
  const toggleCollapsed = useCallback(() => {
    setIsPanelCollapsed(prev => {
      const next = !prev;
      const route = location.pathname.replace(/\/+$/, '');
      localStorage.setItem(`screenShareCollapsed:${route}`, String(next));
      return next;
    });
  }, [location.pathname]);

  /**
   * Set panel position mode
   */
  const setPanelMode = useCallback((mode) => {
    setPanelPosition(mode);
  }, []);

  // Restore collapsed state from localStorage on route change
  useEffect(() => {
    const route = location.pathname.replace(/\/+$/, '');
    const stored = localStorage.getItem(`screenShareCollapsed:${route}`);
    if (stored !== null) {
      setIsPanelCollapsed(stored === 'true');
    } else {
      // Default: collapsed unless on a video-primary route
      setIsPanelCollapsed(!VIDEO_PRIMARY_ROUTES.some(r => route.startsWith(r)));
    }
  }, [location.pathname]);

  // Listen for window updates from window-control feature
  useEffect(() => {
    if (!featureManager) return;

    const windowControl = featureManager.getFeature('window-control');
    if (!windowControl) return;

    // Subscribe to window updates
    const handleWindowUpdate = (windowInfo) => {
      setSelectedWindow(windowInfo);
    };

    const handleForegroundUpdate = (windowInfo) => {
      setForegroundWindow(windowInfo);
    };

    // Subscribe to feature events
    const unsubSelected = windowControl.on('selectedWindowUpdate', handleWindowUpdate);
    const unsubForeground = windowControl.on('foregroundWindowUpdate', handleForegroundUpdate);

    return () => {
      // Cleanup subscriptions
      unsubSelected();
      unsubForeground();
    };
  }, [featureManager]);

  // Pause/resume screen share when panel collapses/expands.
  // Also syncs state on reconnect (e.g. desktop had paused from previous session).
  const sendFeatureMessageRef = useRef(sendFeatureMessage);
  sendFeatureMessageRef.current = sendFeatureMessage;
  useEffect(() => {
    if (!isConnected || !sendFeatureMessageRef.current) return;

    sendFeatureMessageRef.current({
      feature: 'screen-capture',
      version: 'v1.0',
      cmd: isPanelCollapsed ? 'pause_screen_share' : 'resume_screen_share',
      payload: {}
    });
  }, [isPanelCollapsed, isConnected]);

  // Update video presence state
  useEffect(() => {
    const checkInterval = setInterval(() => {
      setHasScreenShare(!!screenShareVideoRef.current?.srcObject);
      setHasCamera(!!cameraVideoRef.current?.srcObject);
    }, 500);

    return () => clearInterval(checkInterval);
  }, []);

  const value = {
    // Panel state
    isPanelVisible,
    isPanelCollapsed,
    panelPosition,
    togglePanel,
    setPanelVisible,
    toggleCollapsed,
    setPanelMode,

    // Video state
    hasScreenShare,
    hasCamera,
    isVideoReady,
    nativeResolution,
    videoAspectRatio,
    setVideoAspectRatio,

    // Window state
    selectedWindow,
    foregroundWindow,
    setSelectedWindow,
    setForegroundWindow,

    // Refs
    screenShareVideoRef,
    cameraVideoRef,
    containerRef,

    // Actions
    registerScreenShareVideo,
    registerCameraVideo,
    updateNativeResolution,
    handleScreenClick,
    getDisplayBounds,

    // Utilities
    coordinateMapper
  };

  return (
    <ScreenShareContext.Provider value={value}>
      {children}
    </ScreenShareContext.Provider>
  );
};

export default ScreenShareContext;
